import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, Star, Phone, Mail, MapPin, LogOut, Users, ArrowLeft, Download, Upload, Info, FileText, Package, DollarSign, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-slate-200 text-slate-200'
          }`}
        />
      ))}
    </div>
  );
};

const ClientiPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [importing, setImporting] = useState(false);
  const [resocontoDialogOpen, setResocontoDialogOpen] = useState(false);
  const [resocontoData, setResocontoData] = useState(null);
  const [loadingResoconto, setLoadingResoconto] = useState(false);
  const [preventivoDetailOpen, setPreventivoDetailOpen] = useState(false);
  const [preventivoDetail, setPreventivoDetail] = useState(null);
  const [ordineDetailOpen, setOrdineDetailOpen] = useState(false);
  const [ordineDetail, setOrdineDetail] = useState(null);
  const [userFormData, setUserFormData] = useState({
    nome: '',
    cognome: '',
    ruolo: 'guest',
    ambito: '',  // DEPRECATO - mantenuto per retrocompatibilità
    ambiti: []   // Nuovo: lista di ambiti
  });
  const [formData, setFormData] = useState({
    nome_azienda: '',
    nome_riferimento: '',
    telefono: '',
    email: '',
    riferimenti_main_solution: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    partita_iva: '',
    codice_fiscale: '',
    pec: '',
    codice_univoco: ''
  });

  useEffect(() => {
    fetchClienti();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchClienti = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clienti`, getAuthHeaders());
      setClienti(response.data);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        onLogout();
      } else {
        toast.error('Errore nel caricamento dei clienti');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      nome_azienda: '',
      nome_riferimento: '',
      telefono: '',
      email: '',
      riferimenti_main_solution: '',
      indirizzo: '',
      cap: '',
      citta: '',
      provincia: '',
      partita_iva: '',
      codice_fiscale: '',
      pec: '',
      codice_univoco: ''
    });
    setEditingCliente(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCliente) {
        await axios.put(`${API}/clienti/${editingCliente.id}`, formData, getAuthHeaders());
        toast.success('Cliente aggiornato con successo');
      } else {
        await axios.post(`${API}/clienti`, formData, getAuthHeaders());
        toast.success('Cliente creato con successo');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchClienti();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio del cliente');
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome_azienda: cliente.nome_azienda,
      nome_riferimento: cliente.nome_riferimento,
      telefono: cliente.telefono,
      email: cliente.email,
      riferimenti_main_solution: cliente.riferimenti_main_solution,
      indirizzo: cliente.indirizzo,
      cap: cliente.cap,
      citta: cliente.citta,
      provincia: cliente.provincia,
      partita_iva: cliente.partita_iva,
      codice_fiscale: cliente.codice_fiscale,
      pec: cliente.pec,
      codice_univoco: cliente.codice_univoco
    });
    setDetailDialogOpen(false);
    setDialogOpen(true);
  };

  const handleDelete = async (clienteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cliente?')) return;
    
    try {
      await axios.delete(`${API}/clienti/${clienteId}`, getAuthHeaders());
      toast.success('Cliente eliminato con successo');
      setDetailDialogOpen(false);
      fetchClienti();
    } catch (error) {
      console.error("Errore nell'eliminazione:", error);
      toast.error("Errore nell'eliminazione del cliente");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/clienti/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Crea link per download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clienti_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('File Excel scaricato con successo');
    } catch (error) {
      console.error('Errore export Excel:', error);
      toast.error('Errore durante l\'esportazione');
    }
  };

  // Scarica template per import clienti
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/clienti/import/template`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_clienti.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template scaricato!');
    } catch (error) {
      console.error('Errore download template:', error);
      toast.error('Errore durante il download del template');
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/clienti/import`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      fetchClienti();
      
      // Reset input
      event.target.value = '';
    } catch (error) {
      console.error('Errore import Excel:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setImporting(false);
    }
  };

  const handleViewResoconto = async (cliente) => {
    try {
      setLoadingResoconto(true);
      setResocontoDialogOpen(true);
      
      const response = await axios.get(`${API}/clienti/${cliente.id}/resoconto`, getAuthHeaders());
      setResocontoData(response.data);
    } catch (error) {
      console.error('Errore caricamento resoconto:', error);
      toast.error('Errore nel caricamento del resoconto');
      setResocontoDialogOpen(false);
    } finally {
      setLoadingResoconto(false);
    }
  };

  const handleViewPreventivoDetail = async (preventivoId) => {
    try {
      const response = await axios.get(`${API}/preventivi/${preventivoId}`, getAuthHeaders());
      setPreventivoDetail(response.data);
      setPreventivoDetailOpen(true);
    } catch (error) {
      console.error('Errore caricamento preventivo:', error);
      toast.error('Errore nel caricamento del preventivo');
    }
  };

  const handleViewOrdineDetail = async (ordineId) => {
    try {
      const response = await axios.get(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      setOrdineDetail(response.data);
      setOrdineDetailOpen(true);
    } catch (error) {
      console.error('Errore caricamento ordine:', error);
      toast.error('Errore nel caricamento dell\'ordine di lavoro');
    }
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleRowClick = (cliente) => {
    setSelectedCliente(cliente);
    setDetailDialogOpen(true);
  };

  // User Management Functions
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
      toast.error('Errore nel caricamento degli utenti');
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    // Supporta sia il vecchio campo ambito che il nuovo ambiti
    const ambiti = userToEdit.ambiti && userToEdit.ambiti.length > 0 
      ? userToEdit.ambiti 
      : (userToEdit.ambito ? [userToEdit.ambito] : []);
    setUserFormData({
      nome: userToEdit.nome,
      cognome: userToEdit.cognome,
      ruolo: userToEdit.ruolo,
      ambito: userToEdit.ambito || '',
      ambiti: ambiti
    });
  };

  const handleUpdateUser = async (userId) => {
    try {
      // Invia solo ambiti (nuovo campo), il backend gestirà la compatibilità
      const dataToSend = {
        ...userFormData,
        ambiti: userFormData.ambiti,
        ambito: userFormData.ambiti.length > 0 ? userFormData.ambiti[0] : null // Retrocompatibilità
      };
      await axios.put(`${API}/users/${userId}`, dataToSend, getAuthHeaders());
      toast.success('Utente aggiornato con successo');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Errore aggiornamento utente:", error);
      toast.error("Errore nell'aggiornamento dell'utente");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeaders());
      toast.success('Utente eliminato con successo');
      fetchUsers();
    } catch (error) {
      console.error("Errore eliminazione utente:", error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione dell'utente");
    }
  };

  const openUsersDialog = () => {
    fetchUsers();
    setUsersDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                data-testid="back-to-dashboard"
                className="mr-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="hidden sm:block bg-blue-600 p-3 rounded-xl shadow-md">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Gestione Clienti</h1>
                <p className="text-sm text-slate-600">PaMa - Benvenuto, {user.nome}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white shadow-md"
                onClick={handleExportExcel}
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta Excel
              </Button>

              <Button 
                variant="outline"
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                onClick={handleDownloadTemplate}
              >
                <Download className="w-4 h-4 mr-2" />
                Scarica Template
              </Button>

              <Button 
                className="bg-orange-600 hover:bg-orange-700 text-white shadow-md"
                disabled={importing}
                onClick={() => document.getElementById('import-excel').click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importazione...' : 'Importa Excel'}
              </Button>
              <input
                id="import-excel"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                className="hidden"
              />
              
              <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-cliente-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Cliente
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Dati Anagrafici */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-900 text-lg">Dati Azienda</h3>
                      <div>
                        <Label htmlFor="nome_azienda">Nome Azienda *</Label>
                        <Input
                          id="nome_azienda"
                          name="nome_azienda"
                          data-testid="input-nome-azienda"
                          value={formData.nome_azienda}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="nome_riferimento">Nome Riferimento *</Label>
                        <Input
                          id="nome_riferimento"
                          name="nome_riferimento"
                          data-testid="input-nome-riferimento"
                          value={formData.nome_riferimento}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="telefono">Telefono *</Label>
                          <Input
                            id="telefono"
                            name="telefono"
                            data-testid="input-telefono"
                            value={formData.telefono}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            data-testid="input-email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="riferimenti_main_solution">Riferimenti Main Solution *</Label>
                        <Input
                          id="riferimenti_main_solution"
                          name="riferimenti_main_solution"
                          data-testid="input-riferimenti-main-solution"
                          value={formData.riferimenti_main_solution}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Sede Legale */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-900 text-lg">Sede Legale</h3>
                      <div>
                        <Label htmlFor="indirizzo">Indirizzo *</Label>
                        <Input
                          id="indirizzo"
                          name="indirizzo"
                          data-testid="input-indirizzo"
                          value={formData.indirizzo}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="cap">CAP *</Label>
                          <Input
                            id="cap"
                            name="cap"
                            data-testid="input-cap"
                            value={formData.cap}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="citta">Città *</Label>
                          <Input
                            id="citta"
                            name="citta"
                            data-testid="input-citta"
                            value={formData.citta}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="provincia">Provincia *</Label>
                          <Input
                            id="provincia"
                            name="provincia"
                            data-testid="input-provincia"
                            value={formData.provincia}
                            onChange={handleInputChange}
                            required
                            maxLength={2}
                            placeholder="Es: MI"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Dati Fatturazione */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-900 text-lg">Dati per Fatturazione</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="partita_iva">Partita IVA *</Label>
                          <Input
                            id="partita_iva"
                            name="partita_iva"
                            data-testid="input-partita-iva"
                            value={formData.partita_iva}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="codice_fiscale">Codice Fiscale *</Label>
                          <Input
                            id="codice_fiscale"
                            name="codice_fiscale"
                            data-testid="input-codice-fiscale"
                            value={formData.codice_fiscale}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="pec">PEC *</Label>
                          <Input
                            id="pec"
                            name="pec"
                            type="email"
                            data-testid="input-pec"
                            value={formData.pec}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="codice_univoco">Codice Univoco *</Label>
                          <Input
                            id="codice_univoco"
                            name="codice_univoco"
                            data-testid="input-codice-univoco"
                            value={formData.codice_univoco}
                            onChange={handleInputChange}
                            required
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleDialogOpenChange(false)}
                        data-testid="cancel-btn"
                      >
                        Annulla
                      </Button>
                      <Button 
                        type="submit"
                        data-testid="submit-cliente-btn"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {editingCliente ? 'Aggiorna' : 'Salva'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-btn"
                className="border-slate-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
            </div>
        </div>
      </div>

      {/* Users Management Dialog */}
      <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Gestione Utenti</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Nome</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Ruolo</TableHead>
                  <TableHead className="font-semibold">Ambito</TableHead>
                  <TableHead className="text-right font-semibold">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      {editingUser?.id === u.id ? (
                        <div className="flex gap-2">
                          <Input
                            value={userFormData.nome}
                            onChange={(e) => setUserFormData({...userFormData, nome: e.target.value})}
                            className="w-24"
                          />
                          <Input
                            value={userFormData.cognome}
                            onChange={(e) => setUserFormData({...userFormData, cognome: e.target.value})}
                            className="w-24"
                          />
                        </div>
                      ) : (
                        `${u.nome} ${u.cognome}`
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{u.email}</TableCell>
                    <TableCell>
                      {editingUser?.id === u.id ? (
                        <Select 
                          value={userFormData.ruolo} 
                          onValueChange={(value) => setUserFormData({...userFormData, ruolo: value})}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="guest">Guest</SelectItem>
                            <SelectItem value="master">Master</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.ruolo === 'admin' ? 'bg-red-100 text-red-700' :
                          u.ruolo === 'master' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {u.ruolo}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingUser?.id === u.id ? (
                        <div className="flex flex-wrap gap-2">
                          {['amministrativo', 'ingegnere', 'cantierista', 'fattibilita'].map(amb => (
                            <label key={amb} className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userFormData.ambiti.includes(amb)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setUserFormData({...userFormData, ambiti: [...userFormData.ambiti, amb]});
                                  } else {
                                    setUserFormData({...userFormData, ambiti: userFormData.ambiti.filter(a => a !== amb)});
                                  }
                                }}
                                className="rounded border-slate-300"
                              />
                              <span className="text-xs capitalize">{amb === 'fattibilita' ? 'Fattibilità' : amb}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        (() => {
                          // Mostra ambiti (nuovo) o ambito (vecchio)
                          const ambiti = u.ambiti && u.ambiti.length > 0 ? u.ambiti : (u.ambito ? [u.ambito] : []);
                          return ambiti.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {ambiti.map(amb => (
                                <span key={amb} className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  amb === 'amministrativo' ? 'bg-amber-100 text-amber-700' :
                                  amb === 'ingegnere' ? 'bg-blue-100 text-blue-700' :
                                  amb === 'cantierista' ? 'bg-green-100 text-green-700' :
                                  amb === 'fattibilita' ? 'bg-purple-100 text-purple-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {amb === 'fattibilita' ? 'Fattibilità' : amb}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          );
                        })()
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editingUser?.id === u.id ? (
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateUser(u.id)}
                            data-testid={`save-user-${u.id}`}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Salva
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingUser(null)}
                          >
                            Annulla
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(u)}
                            data-testid={`edit-user-${u.id}`}
                            className="hover:bg-blue-50 hover:text-blue-600"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {u.ruolo !== 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id)}
                              data-testid={`delete-user-${u.id}`}
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl">
          {selectedCliente && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-2xl">{selectedCliente.nome_azienda}</DialogTitle>
                    <p className="text-sm text-slate-600 mt-1 font-mono">{selectedCliente.numero_ms}</p>
                  </div>
                  <StarRating rating={selectedCliente.rating} />
                </div>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Contatti */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-slate-900 mb-3">Contatti</h3>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-2 rounded-lg">
                        <Building2 className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Nome Riferimento</p>
                        <p className="font-medium">{selectedCliente.nome_riferimento}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-2 rounded-lg">
                        <Phone className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Telefono</p>
                        <p className="font-medium">{selectedCliente.telefono}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-2 rounded-lg">
                        <Mail className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium">{selectedCliente.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-2 rounded-lg">
                        <Building2 className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Riferimenti Main Solution</p>
                        <p className="font-medium">{selectedCliente.riferimenti_main_solution}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sede Legale */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Sede Legale</h3>
                  <div className="flex items-start gap-3">
                    <div className="bg-white p-2 rounded-lg">
                      <MapPin className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{selectedCliente.indirizzo}</p>
                      <p className="text-slate-600">
                        {selectedCliente.cap} {selectedCliente.citta} ({selectedCliente.provincia})
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dati Fatturazione */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Partita IVA</p>
                    <p className="font-mono font-medium">{selectedCliente.partita_iva}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Codice Fiscale</p>
                    <p className="font-mono font-medium">{selectedCliente.codice_fiscale}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">PEC</p>
                    <p className="font-medium">{selectedCliente.pec}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Codice Univoco</p>
                    <p className="font-mono font-medium">{selectedCliente.codice_univoco}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleEdit(selectedCliente)}
                  data-testid="detail-edit-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifica
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDelete(selectedCliente.id)}
                  data-testid="detail-delete-btn"
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Resoconto Dialog */}
      <Dialog open={resocontoDialogOpen} onOpenChange={setResocontoDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Resoconto Cliente</DialogTitle>
            {resocontoData && (
              <p className="text-sm text-slate-600 mt-1">
                {resocontoData.cliente.nome_azienda} - {resocontoData.cliente.numero_ms}
              </p>
            )}
          </DialogHeader>

          {loadingResoconto ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Caricamento resoconto...</p>
            </div>
          ) : resocontoData ? (
            <div className="space-y-6 mt-4">
              {/* Metriche Principali */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    <p className="text-xs text-amber-700 font-medium">Preventivi Totali</p>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{resocontoData.preventivi_totali}</p>
                </div>

                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    <p className="text-xs text-green-700 font-medium">Preventivi Accettati</p>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{resocontoData.preventivi_accettati}</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    <p className="text-xs text-blue-700 font-medium">Ordini di Lavoro</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{resocontoData.ordini_totali}</p>
                </div>

                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <p className="text-xs text-emerald-700 font-medium">Fatturato</p>
                  </div>
                  <p className="text-xl font-bold text-emerald-900">
                    € {resocontoData.fatturato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-orange-600" />
                    <p className="text-xs text-orange-700 font-medium">Da Fatturare</p>
                  </div>
                  <p className="text-xl font-bold text-orange-900">
                    € {resocontoData.da_fatturare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Dettaglio Preventivi */}
              {resocontoData.dettagli_preventivi.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Dettaglio Preventivi
                  </h3>
                  <div className="bg-white rounded-lg overflow-hidden border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Numero</TableHead>
                          <TableHead className="font-semibold">Categoria</TableHead>
                          <TableHead className="font-semibold">Prezzo</TableHead>
                          <TableHead className="font-semibold">Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resocontoData.dettagli_preventivi.map((prev, idx) => (
                          <TableRow key={idx}>
                            <TableCell 
                              className="font-mono text-amber-600 hover:text-amber-800 cursor-pointer font-semibold hover:underline"
                              onClick={() => handleViewPreventivoDetail(prev.id)}
                            >
                              {prev.numero_preventivo}
                            </TableCell>
                            <TableCell>{prev.categoria}</TableCell>
                            <TableCell className="font-semibold">
                              € {parseFloat(prev.prezzo_manuale).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                prev.stato.toLowerCase() === 'accettato' ? 'bg-green-100 text-green-700' :
                                prev.stato.toLowerCase() === 'rifiutato' ? 'bg-red-100 text-red-700' :
                                prev.stato.toLowerCase() === 'in attesa' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {prev.stato}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Dettaglio Ordini */}
              {resocontoData.dettagli_ordini.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Dettaglio Ordini di Lavoro
                  </h3>
                  <div className="bg-white rounded-lg overflow-hidden border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Numero</TableHead>
                          <TableHead className="font-semibold">Descrizione</TableHead>
                          <TableHead className="font-semibold">Importo</TableHead>
                          <TableHead className="font-semibold">Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resocontoData.dettagli_ordini.map((ord, idx) => (
                          <TableRow key={idx}>
                            <TableCell 
                              className="font-mono text-blue-600 hover:text-blue-800 cursor-pointer font-semibold hover:underline"
                              onClick={() => handleViewOrdineDetail(ord.id)}
                            >
                              {ord.numero_ordine}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{ord.descrizione}</TableCell>
                            <TableCell className="font-semibold">
                              € {parseFloat(ord.importo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                ord.stato.toLowerCase() === 'completato' ? 'bg-green-100 text-green-700' :
                                ord.stato.toLowerCase() === 'in corso' ? 'bg-blue-100 text-blue-700' :
                                ord.stato.toLowerCase() === 'in attesa' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {ord.stato}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Empty States */}
              {resocontoData.dettagli_preventivi.length === 0 && resocontoData.dettagli_ordini.length === 0 && (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <p className="text-slate-600">Nessun preventivo o ordine di lavoro per questo cliente</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Preventivo Detail Dialog */}
      <Dialog open={preventivoDetailOpen} onOpenChange={setPreventivoDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {preventivoDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">Dettaglio Preventivo</DialogTitle>
                <p className="text-sm text-slate-600 mt-1 font-mono">{preventivoDetail.numero_preventivo}</p>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Cliente</p>
                    <p className="font-semibold">{preventivoDetail.cliente_nome}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Categoria</p>
                    <p className="font-semibold">{preventivoDetail.categoria}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Prezzo</p>
                    <p className="font-semibold text-lg text-emerald-600">
                      € {parseFloat(preventivoDetail.prezzo_manuale).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Stato</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      preventivoDetail.stato.toLowerCase() === 'accettato' ? 'bg-green-100 text-green-700' :
                      preventivoDetail.stato.toLowerCase() === 'rifiutato' ? 'bg-red-100 text-red-700' :
                      preventivoDetail.stato.toLowerCase() === 'in attesa' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {preventivoDetail.stato}
                    </span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">Descrizione</p>
                  <p className="text-sm">{preventivoDetail.descrizione || 'Nessuna descrizione'}</p>
                </div>

                {preventivoDetail.note && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Note</p>
                    <p className="text-sm">{preventivoDetail.note}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Data Inizio Validità</p>
                    <p className="font-medium">{preventivoDetail.data_inizio_validita || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Data Fine Validità</p>
                    <p className="font-medium">{preventivoDetail.data_fine_validita || '-'}</p>
                  </div>
                </div>

                {preventivoDetail.promemoria_attivo && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-amber-600" />
                      <p className="font-semibold text-amber-900">Promemoria Attivo</p>
                    </div>
                    <p className="text-sm text-amber-700">
                      Data: {preventivoDetail.promemoria_data || '-'}
                    </p>
                    <p className="text-sm text-amber-700">
                      Nota: {preventivoDetail.promemoria_nota || '-'}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Ordine Lavoro Detail Dialog */}
      <Dialog open={ordineDetailOpen} onOpenChange={setOrdineDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {ordineDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">Dettaglio Ordine di Lavoro</DialogTitle>
                <p className="text-sm text-slate-600 mt-1 font-mono">{ordineDetail.numero_ordine}</p>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Cliente</p>
                    <p className="font-semibold">{ordineDetail.cliente_nome}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Stato</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      ordineDetail.stato?.toLowerCase() === 'completato' ? 'bg-green-100 text-green-700' :
                      ordineDetail.stato?.toLowerCase() === 'in corso' ? 'bg-blue-100 text-blue-700' :
                      ordineDetail.stato?.toLowerCase() === 'in attesa' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {ordineDetail.stato || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">Descrizione Lavori</p>
                  <p className="text-sm">{ordineDetail.descrizione || 'Nessuna descrizione'}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-xs text-emerald-700 mb-1">Importo Totale</p>
                    <p className="font-bold text-xl text-emerald-900">
                      € {ordineDetail.importo_totale ? parseFloat(ordineDetail.importo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Data Inizio</p>
                    <p className="font-medium">{ordineDetail.data_inizio || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Data Consegna</p>
                    <p className="font-medium">{ordineDetail.data_consegna || '-'}</p>
                  </div>
                </div>

                {ordineDetail.sal_list && ordineDetail.sal_list.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">SAL (Stati Avanzamento Lavori)</h3>
                    <div className="space-y-2">
                      {ordineDetail.sal_list.map((sal, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{sal.descrizione || 'SAL ' + (idx + 1)}</p>
                              <p className="text-sm text-slate-600">Data: {sal.data || '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">
                                € {sal.importo ? parseFloat(sal.importo).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                              </p>
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                sal.stato === 'fatturato' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {sal.stato === 'fatturato' ? 'Fatturato' : 'Da Fatturare'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {ordineDetail.note && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Note</p>
                    <p className="text-sm">{ordineDetail.note}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Caricamento...</p>
          </div>
        ) : clienti.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Nessun cliente presente</h3>
            <p className="text-slate-600 mb-6">Inizia aggiungendo il tuo primo cliente</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="empty-state-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Cliente
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Codice MS</TableHead>
                  <TableHead className="font-semibold">Nome Azienda</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Telefono</TableHead>
                  <TableHead className="text-center font-semibold">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clienti.map((cliente) => (
                  <TableRow 
                    key={cliente.id} 
                    data-testid={`cliente-row-${cliente.numero_ms}`}
                    className="hover:bg-slate-50"
                  >
                    <TableCell 
                      className="font-mono font-semibold text-blue-600 cursor-pointer"
                      onClick={() => handleRowClick(cliente)}
                    >
                      {cliente.numero_ms}
                    </TableCell>
                    <TableCell 
                      className="font-medium cursor-pointer"
                      onClick={() => handleRowClick(cliente)}
                    >
                      {cliente.nome_azienda}
                    </TableCell>
                    <TableCell 
                      className="text-sm cursor-pointer"
                      onClick={() => handleRowClick(cliente)}
                    >
                      {cliente.email}
                    </TableCell>
                    <TableCell 
                      className="font-medium cursor-pointer"
                      onClick={() => handleRowClick(cliente)}
                    >
                      {cliente.telefono}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewResoconto(cliente);
                        }}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <Info className="w-4 h-4 mr-1" />
                        Resoconto
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientiPage;
